import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';
import { toast, ToastContainer, Slide } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import "./toast.css"
import axios from 'axios';
import SubmissionDetails from './components/submissionDialogs/submissionDetails';
import { Typography, 
          Box, 
          Table, 
          TableContainer, 
          TableCell, 
          TableRow, 
          TableHead,
          TableBody, 
          Button, Grid2,
          Radio,
          TablePagination,
          Dialog,
          useMediaQuery,
          IconButton} from '@mui/material';

const columns = [
  { id: 'radioButtons', minWidth: 10},
  { id: 'organization', label: 'Organization', minWidth: 180},
  { id: 'subType', label: 'Submission Type', minWidth: 140},
  { id: 'fileName', label: 'File Name', minWidth: 270},
  { id: 'lastUpdated', label: 'Last Updated', minWidth: 200},
  { id: 'validation', label: 'Validation', minWidth: 110},
  { id: 'status', label: 'Submission Status', minWidth: 127}
];

const Home = ({user}) => {
  const {email} = user !== undefined ? user : {};
  const serverHost = process.env.REACT_APP_BACKEND_SERVER_HOST || 'https://localhost:3001';
  const itemsPerPage = 10;
  const location = useLocation();
  const [submissions, setSubmissions] = useState([]);
  const [selectedValue, setSelectedValue] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage);
  const [uploadType, setUploadType ] = useState("");
  const [bannerShow, setBannerShow] = useState(false);
  const [fileName, setFileName] = useState("");
  const [viewDetails, setViewDetails] = useState({});
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const fullScreen = useMediaQuery('(max-width:600px)');

  const handleDetailsDialogOpen = () => setShowDetailsDialog(true);
  const handleDetailsDialogClose = () => setShowDetailsDialog(false); 

  const changeTypeName = useCallback((typeName) => {
    const changeTrack = {
      'dataset': 'Dataset',
      'track1':  'Document Validation',
      'track2':  'Match to ID',
      'track3':  'Passive PAD'
    }
    return changeTrack[typeName] || typeName; 
   }, [])

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await axios.get(`${serverHost}/submissions/${email}`, { withCredentials: true });
      if (response.status === 200) {
          setSubmissions(response.data.sort((a, b) => new Date(b.upload_timestamp) - new Date(a.upload_timestamp)));

          for(let i = 0; i < response.data.length; i++) {
            if(!response.data[i].hasOwnProperty('approval_status')) {
              setBannerShow(true);
              setFileName(response.data[i].upload_key.split('/').reverse()[0]);
              setUploadType(changeTypeName(response.data[i].upload_type));
            } 
          }
          
      }
    } catch (error) {
      console.error('Error fetching submissions:', error.response ? error.response.data : error.message);
    }
  }, [serverHost, email, changeTypeName]);

  const currentPageData = submissions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  
  
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  }

  const closeBanner = () => {
    setBannerShow(false);
  }

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0)
  }

  const handleRadioChange = (event, submission) => {
    setSelectedValue(submission.upload_key);
    setViewDetails(submission);
  }

  useEffect(() => {
    const toastOptions = {
      position: "bottom-center",
      autoClose: 8000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
      transition: Slide
    }

    if(location.state?.formSubmitted) {
        fetchSubmissions();
        toast.success("Files uploaded successfully.  Submission complete.", toastOptions);
    } else {
      fetchSubmissions();
    }
  }, [fetchSubmissions, location.state]);

  return (
    <Box className='container-box'>
      <Box className='banner-notice' sx={{display: bannerShow ? 'flex' : 'none'}}>
        <Typography sx={{fontSize: '20px', paddingLeft: '30px'}} color="fontTheme">
          NOTICE
        </Typography>
        <Typography color="fontTheme" sx={{fontSize: '12px', padding: '18px'}}>
          YOUR RECENT {uploadType.toUpperCase()} SUBMISSION {fileName.toUpperCase()} IS BEING REVIEWED. A SYSTEM NOTIFICATION WILL BE SENT TO YOUR VERIFIED EMAIL WHEN THERE IS A STATUS CHANGE. 
          IN ADDITION YOU CAN CHECK THE SUMMARY STATUS TABLE AT ANYTIME. PLEASE NOTE THAT NO ADDITIONAL {uploadType.toUpperCase()} CAN BE SUBMITTED DURING THE SUBMISSION REVIEW PROCESS.
        </Typography>
        <IconButton onClick={closeBanner} color="fontTheme" sx={{paddingLeft: '24px', paddingRight: '50px'}} size="large">
            <CloseIcon />
          </IconButton>
      </Box>
      <Box sx={{marginTop: bannerShow ? '12px' : '92px'}} >
          <Typography color="fontTheme" 
                      className='page-header'>MyConsole</Typography>
          <Typography className='page-description'>
            This console enables you to submit data and/or technology for testing and evaluation (T&E) activities at the MdTF and to track the status of your submissions.  
            The main dashboard shows a summary of your submissions.  To view the details of your submission, select the radio button and proceed by selecting an available 
            action button. To make a new submission, select an available submission option from the panel on the left.  To ensure you are working with the most up-to-date 
            information, select the 'Refresh Table' button.  Notifications regarding changes in submission status will be sent to your registered email address.
          </Typography>
      </Box>
      <Box className='below-content'>
        <Grid2 container spacing={2} sx={{justifyContent: 'space-between'}}>
            <Grid2 item sm={12} md={6} lg={4} sx={{justifyContent: 'space-between'}}>
                <Typography color="fontTheme"
                            variant="h5" 
                            marginBottom="12px"
                            className='page-header'>
                    Submission Summary ({submissions.length})
                </Typography>
            </Grid2>
            <Grid2 item sm={12} md={6} lg={8} sx={{justifyContent:'end', display:'flex', paddingBottom: '15px'}}>
                <Box className='table-buttons-div'>
                    <Button className="table-buttons"  variant="outlined" onClick={() => handleDetailsDialogOpen()} disabled={Object.keys(viewDetails).length === 0}>VIEW DETAILS</Button>
                </Box>
                <Box className='table-buttons-div'>
                    <Button className="table-buttons" component={Link} onClick={() => fetchSubmissions()} variant="outlined">REFRESH TABLE</Button>
                </Box>
            </Grid2>
        </Grid2>
        <TableContainer>
          <Table sx={{width: '100%', border: 1, borderColor: '#E0E0E0'}}>
            <TableHead sx={{backgroundColor:'#F2F0F0'}}>
              <TableRow className='table-text' sx={{'.MuiTableCell-root': {fontWeight: 'bold'}}}>
                {columns.map((column) => (
                    <TableCell
                      className='table-text'
                      key={column.id} 
                      style={{minWidth: column.minWidth, paddingTop: '16px', paddingBottom: '16px'}}>
                        {column.label} 
                    </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
            {currentPageData.map((submission, index) => (
                <TableRow 
                  hover
                  key={index}
                  onClick={(event) => handleRadioChange(event, submission)}
                  role="radio"
                >
                  <TableCell className='table-text' sx={{width: '0px'}}>
                    <Radio checked={selectedValue === submission.upload_key} color='fontTheme' sx={{padding: '13px', marginLeft: '13px', marginRight: '25px'}}/>
                  </TableCell>
                  <TableCell className='table-text'>{submission.company_name}</TableCell>
                  <TableCell className='table-text'>{submission.upload_type ? changeTypeName(submission.upload_type) : ''}</TableCell>
                  <TableCell className='table-text'>{submission.upload_key ? submission.upload_key.split('/').reverse()[0] : ''}</TableCell>
                  <TableCell className='table-text'>{submission.last_updated ? new Date(submission.last_updated).toLocaleString() : ''}</TableCell>
                  <TableCell className='table-text'>{submission.validation_status ? submission.validation_status.charAt(0) + submission.validation_status.slice(1).toLowerCase() : ''}</TableCell>
                  <TableCell className='table-text'>{submission.approval_status ? submission.approval_status.charAt(0) + submission.approval_status.slice(1).toLowerCase() : 'Submission In Review'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div"
                                  count={submissions.length}
                                  page={page}
                                  onPageChange={handlePageChange}
                                  rowsPerPage={rowsPerPage}
                                  onRowsPerPageChange={handleRowsPerPageChange}
                                  rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Box>
      <ToastContainer autoClose={5000} />

      {/* SUBMISSION DETAILS DIALOG */}
      <Dialog open={showDetailsDialog}
              onClose={handleDetailsDialogClose}
              aria-labelledby='alert-dialog-title'
              aria-describedby='alert-dialog-description'
              fullScreen={fullScreen}
      >
        <SubmissionDetails 
          organization={viewDetails?.company_name} 
          subType={viewDetails?.upload_type} 
          fileName={viewDetails?.upload_key} 
          email={viewDetails?.company_email}
          onCloseSub={handleDetailsDialogClose}
        />
      </Dialog>
    </Box>
  );
}

export default Home;
